/**
 * Answering an identity question by merging, with the confirm step he asked for.
 *
 * THE SHAPE OF THIS IS DRIVEN BY WHAT THE LIVE VAULT ACTUALLY CONTAINS, measured on
 * 2026-08-04. Two findings decided it:
 *
 *  - `dvega@arkospb.com` is on four notes: `David Vega`, `D. Vega`, `Diana Vega` and
 *    `Dvega`. That is two real people plus two abbreviations of one of them. So there is
 *    deliberately NO "merge this group" control. A group is a place to look; a merge is
 *    one named note into one other named note, chosen here, previewed before it runs.
 *  - `mohanad@ntamgroup.com` was on eleven notes, because ingestion is forward-driven
 *    and his own address rides on everything he forwards. The server refuses that case
 *    with a message, and this renders the message instead of a merge form.
 *
 * So the flow is: pick who survives, pick who folds in, read the plan, confirm. Three
 * screens' worth of decision in one panel, and nothing is written before the last click.
 *
 * WHAT IT WILL NOT DO. It never preselects what to merge AWAY. The survivor is
 * suggested, because keeping the better-established note is safe under any reading, but
 * choosing what disappears is the irreversible half and stays entirely his.
 */

import { useEffect, useState } from 'react';
import {
  applyMerge,
  fetchIdentityGroup,
  fetchMergePlan,
  type IdentityGroup,
  type MergeCandidate,
  type MergePlan,
} from './api.js';
import { Icon } from './Icon.js';

type State =
  | { kind: 'loading' }
  | { kind: 'refused'; message: string }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; group: IdentityGroup };

export function MergePanel({
  ref_,
  onMerged,
  onCancel,
}: {
  /** The attention item's ref, `mo:identity:<email>`. */
  ref_: string;
  /** Called once a merge has actually been written, so the caller can resolve the item. */
  onMerged: (survivorName: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [survivor, setSurvivor] = useState('');
  const [folding, setFolding] = useState('');
  const [plan, setPlan] = useState<MergePlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    let live = true;
    void fetchIdentityGroup(ref_).then((r) => {
      if (!live) return;
      if (r.state === 'ready') {
        setState({ kind: 'ready', group: r.value });
        // The survivor is suggested; what folds in is not, so it starts empty.
        setSurvivor(r.value.recommended);
        return;
      }
      if (r.state === 'refused') {
        setState({ kind: 'refused', message: r.message });
        return;
      }
      // `Loaded` includes a 'loading' variant that this fetch never returns, so the
      // message is read defensively rather than asserted to exist.
      setState({ kind: 'error', message: 'message' in r ? r.message : 'unknown error' });
    });
    return () => {
      live = false;
    };
  }, [ref_]);

  // Any change to either side invalidates a shown plan. Without this, he could preview
  // A into B, switch the target, and confirm a merge the preview never described.
  useEffect(() => {
    setPlan(null);
    setProblem('');
  }, [survivor, folding]);

  if (state.kind === 'loading') return <p className="mo-empty">Looking at the records…</p>;

  if (state.kind === 'refused') {
    return (
      <div className="mo-merge">
        <p className="mo-merge-refused">
          <Icon name="judgement" size={14} /> {state.message}
        </p>
        <p className="mo-merge-actions">
          <button type="button" className="mo-btn" onClick={onCancel}>
            Close
          </button>
        </p>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="mo-merge">
        <p className="mo-merge-problem">Could not load the records: {state.message}</p>
        <p className="mo-merge-actions">
          <button type="button" className="mo-btn" onClick={onCancel}>
            Close
          </button>
        </p>
      </div>
    );
  }

  const { group } = state;
  const byPath = (p: string): MergeCandidate | undefined => group.candidates.find((c) => c.path === p);
  const survivorC = byPath(survivor);
  const foldingC = byPath(folding);

  const preview = async (): Promise<void> => {
    setBusy(true);
    const r = await fetchMergePlan(folding, survivor);
    setBusy(false);
    if (r.ok) setPlan(r.value);
    else setProblem(r.error);
  };

  const confirm = async (): Promise<void> => {
    setBusy(true);
    const r = await applyMerge(folding, survivor);
    setBusy(false);
    if (r.ok) onMerged(survivorC?.name ?? '');
    else setProblem(r.error);
  };

  return (
    <div className="mo-merge">
      <p className="mo-merge-why">
        <strong>{group.email}</strong> is on {group.candidates.length} notes. {group.why}
      </p>

      {group.caution && (
        <p className="mo-merge-caution">
          <Icon name="judgement" size={14} /> At least one of these does not look like a
          spelling of the same name. A shared address can mean a shared mailbox or two
          colleagues, not one person. Check before merging: a wrong merge is recoverable
          from git, but the brain will repeat it as fact until you notice.
        </p>
      )}

      <table className="mo-merge-table">
        <thead>
          <tr>
            <th scope="col">Keep</th>
            <th scope="col">Merge away</th>
            <th scope="col">Note</th>
            <th scope="col">Linked from</th>
          </tr>
        </thead>
        <tbody>
          {group.candidates.map((c) => (
            <tr key={c.path} data-variant={c.looksLikeVariant}>
              <td>
                <input
                  type="radio"
                  name="mo-merge-survivor"
                  aria-label={`Keep ${c.name}`}
                  checked={survivor === c.path}
                  onChange={() => {
                    setSurvivor(c.path);
                    // Keeping and folding away cannot be the same note.
                    if (folding === c.path) setFolding('');
                  }}
                />
              </td>
              <td>
                <input
                  type="radio"
                  name="mo-merge-folding"
                  aria-label={`Merge away ${c.name}`}
                  checked={folding === c.path}
                  disabled={survivor === c.path}
                  onChange={() => setFolding(c.path)}
                />
              </td>
              <td>
                <span className="mo-merge-name">{c.name}</span>
                <span className="mo-merge-meta">
                  {c.hasBody ? 'has notes' : 'empty stub'}
                  {!c.looksLikeVariant && <span className="mo-merge-flag">different name</span>}
                </span>
              </td>
              <td className="mo-merge-links">{c.inboundLinks}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {problem !== '' && <p className="mo-merge-problem">{problem}</p>}

      {plan !== null && (
        <div className="mo-merge-plan">
          <p className="mo-merge-plan-head">This is exactly what will happen:</p>
          <ul>
            {plan.summary.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
          {plan.relinked.length > 0 && (
            <p className="mo-merge-meta">
              {plan.relinked.length} other note{plan.relinked.length === 1 ? '' : 's'} will have
              links repointed.
            </p>
          )}
          <p className="mo-merge-meta">
            Nothing is deleted. {foldingC?.name} becomes a tombstone pointing at{' '}
            {survivorC?.name}, so this is reversible from git.
          </p>
        </div>
      )}

      <p className="mo-merge-actions">
        {plan === null ? (
          <button
            type="button"
            className="mo-btn mo-btn-primary"
            disabled={busy || folding === '' || survivor === '' || folding === survivor}
            onClick={() => void preview()}
          >
            {busy ? 'Working…' : 'Preview the merge'}
          </button>
        ) : (
          <button
            type="button"
            className="mo-btn mo-btn-primary"
            disabled={busy}
            onClick={() => void confirm()}
          >
            {busy ? 'Merging…' : `Merge ${foldingC?.name} into ${survivorC?.name}`}
          </button>
        )}
        <button type="button" className="mo-btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </p>
    </div>
  );
}
